import { useGetFinancingDataQuery, usePostOneTimePaymentMutation } from '@/services/accounts';
import { Box, Button, DateInput, Icon, Modal, SelectInput, TextInput, toast } from '@palmetto/palmetto-components';
import { FormikValues, useFormik } from 'formik';
import { DateTime } from 'luxon';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { useAuth } from '../../auth/authProvider';
import { isPalmettoFinanceUserIncludingRoot } from '../../auth/RequireIsInternalUser';

import { CancelPendingPaymentModal, PendingPaymentDetails } from './CancelPendingPaymentModal';

const startOfToday = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const MONTH_OPTIONS = [
  { label: '01 - January', value: '01' },
  { label: '02 - February', value: '02' },
  { label: '03 - March', value: '03' },
  { label: '04 - April', value: '04' },
  { label: '05 - May', value: '05' },
  { label: '06 - June', value: '06' },
  { label: '07 - July', value: '07' },
  { label: '08 - August', value: '08' },
  { label: '09 - September', value: '09' },
  { label: '10 - October', value: '10' },
  { label: '11 - November', value: '11' },
  { label: '12 - December', value: '12' },
];

const BANK_ACCOUNT_TYPE_OPTIONS = [
  { label: 'Checking', value: 'checking' },
  { label: 'Savings', value: 'savings' },
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 20 }, (_, i) => ({
  label: String(currentYear + i),
  value: String(currentYear + i),
}));

const focusField = (id: string): void => {
  setTimeout(() => {
    (document.getElementById(id) as HTMLInputElement | null)?.focus();
  }, 0);
};

export const OneTimePaymentModal = ({
  isModalOpen,
  closeModal,
  defaultAmount,
  amountPastDue,
  hasConcordAccountBeenCreated,
}: {
  isModalOpen: boolean;
  closeModal: (event?: SyntheticEvent) => void;
  defaultAmount?: string;
  amountPastDue?: string;
  hasConcordAccountBeenCreated?: boolean;
}) => {
  const { id = '' } = useParams<{ id: string }>();
  const { claims } = useAuth();
  const [postOneTimePayment] = usePostOneTimePaymentMutation();
  const { data: financingData, isLoading: isCheckingPending } = useGetFinancingDataQuery(id, {
    skip: !isModalOpen || !id || !isPalmettoFinanceUserIncludingRoot(claims) || !hasConcordAccountBeenCreated,
  });
  const [showCardNumber, setShowCardNumber] = useState(false);
  const [showCvc, setShowCvc] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [paymentType, setPaymentType] = useState<'card' | 'ach'>('card');
  const [cancelTarget, setCancelTarget] = useState<PendingPaymentDetails | null>(null);

  const validationSchema = useMemo(
    () =>
      yup.object({
        cardHolderName:
          paymentType === 'card' ? yup.string().required('Card holder name is required') : yup.string(),
        number:
          paymentType === 'card'
            ? yup
                .string()
                .required('Card number is required')
                .matches(/^\d{13,19}$/, 'Card number must be 13–19 digits')
            : yup.string(),
        expirationMonth:
          paymentType === 'card'
            ? yup
                .string()
                .required('Expiration month is required')
                .matches(/^(0[1-9]|1[0-2])$/, 'Must be 01–12')
            : yup.string(),
        expirationYear:
          paymentType === 'card'
            ? yup
                .string()
                .required('Expiration year is required')
                .matches(/^\d{4}$/, 'Must be 4 digits')
                .test('notExpired', 'Card is expired', function (year) {
                  const month = this.parent.expirationMonth;
                  if (!year || !month) return true;
                  const now = new Date();
                  const expiry = new Date(Number(year), Number(month) - 1, 1);
                  return expiry >= new Date(now.getFullYear(), now.getMonth(), 1);
                })
            : yup.string(),
        cvc:
          paymentType === 'card'
            ? yup
                .string()
                .required('CVC is required')
                .matches(/^\d{3,4}$/, 'Must be 3–4 digits')
            : yup.string(),
        zipCode:
          paymentType === 'card'
            ? yup
                .string()
                .required('Billing zip code is required')
                .matches(/^\d{5}$/, 'Must be exactly 5 digits')
            : yup.string(),
        accountHolderName:
          paymentType === 'ach' ? yup.string().required('Account holder name is required') : yup.string(),
        routingNumber:
          paymentType === 'ach'
            ? yup
                .string()
                .required('Routing number is required')
                .matches(/^\d+$/, 'Routing number must contain only numbers')
                .matches(/^\d{9}$/, 'A valid U.S. routing number is 9 digits')
                .test('firstDigit', 'The first digit must be 0, 1, 2, or 3', (val) => /^[0123]/.test(val || ''))
            : yup.string(),
        accountNumber:
          paymentType === 'ach'
            ? yup
                .string()
                .required('Account number is required')
                .matches(/^\d{4,17}$/, 'Must be 4–17 digits')
            : yup.string(),
        confirmAccountNumber:
          paymentType === 'ach'
            ? yup
                .string()
                .oneOf([yup.ref('accountNumber')], 'The Account Numbers must match')
                .required('The Account Number needs to be confirmed')
            : yup.string(),
        bankAccountType:
          paymentType === 'ach'
            ? yup.string().oneOf(['checking', 'savings']).required('Account type is required')
            : yup.string(),
        amount: yup
          .number()
          .typeError('Amount must be a number')
          .required('Amount is required')
          .positive('Amount must be greater than $0.00'),
        paymentDate: yup
          .mixed<Date>()
          .required('Payment date is required')
          .test('futureDate', 'Payment date cannot be in the past', (value) => {
            if (!value) return false;
            return value >= startOfToday();
          }),
      }),
    [paymentType],
  );

  const openCancelModal = useCallback((payment: PendingPaymentDetails) => {
    setCancelTarget(payment);
  }, []);

  const handleOpenCancelForPending = useCallback(() => {
    if (!financingData?.pendingPayments?.[0]) return;
    openCancelModal({
      paymentId: financingData.pendingPayments[0].paymentId,
      amount: financingData.pendingPayments[0].amount,
      paymentDate: financingData.pendingPayments[0].paymentDate,
      entryDate: financingData.pendingPayments[0].entryDate,
      paymentTypeDescription: financingData.pendingPayments[0].paymentTypeDescription,
    });
  }, [financingData?.pendingPayments, openCancelModal]);

  const closeCancelModal = useCallback(() => {
    setCancelTarget(null);
  }, []);

  const handleCancelled = useCallback(() => {
    setCancelTarget(null);
    closeModal();
  }, [closeModal]);

  const getDefaultPreset = useCallback(() => {
    if (defaultAmount) return 'total' as const;
    if (amountPastDue) return 'pastDue' as const;
    return 'custom' as const;
  }, [defaultAmount, amountPastDue]);

  const [amountPreset, setAmountPreset] = useState<'pastDue' | 'total' | 'custom'>(getDefaultPreset);

  useEffect(() => {
    if (isModalOpen) {
      setAmountPreset(getDefaultPreset());
    }
  }, [isModalOpen, getDefaultPreset]);

  const formik = useFormik({
    initialValues: {
      cardHolderName: '',
      number: '',
      expirationMonth: '',
      expirationYear: '',
      cvc: '',
      zipCode: '',
      accountHolderName: '',
      routingNumber: '',
      accountNumber: '',
      confirmAccountNumber: '',
      bankAccountType: '' as 'checking' | 'savings' | '',
      amount: defaultAmount ? parseFloat(defaultAmount).toFixed(2) : '',
      paymentDate: null as Date | null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values: FormikValues, { setSubmitting, resetForm }) => {
      try {
        if (paymentType === 'ach') {
          await postOneTimePayment({
            accountId: id,
            amount: Number(values.amount),
            paymentDate: DateTime.fromJSDate(values.paymentDate as Date).toISODate() ?? '',
            bankAccount: {
              accountNumber: values.accountNumber,
              routingNumber: values.routingNumber,
              bankAccountType: values.bankAccountType as 'checking' | 'savings',
              accountHolderName: values.accountHolderName,
            },
          }).unwrap();
        } else {
          await postOneTimePayment({
            accountId: id,
            amount: Number(values.amount),
            paymentDate: DateTime.fromJSDate(values.paymentDate as Date).toISODate() ?? '',
            card: {
              number: values.number,
              expirationMonth: values.expirationMonth,
              expirationYear: values.expirationYear,
              cvc: values.cvc,
              cardHolderName: values.cardHolderName,
              zipCode: values.zipCode,
            },
          }).unwrap();
        }
        toast.success('One-time payment scheduled successfully');
        resetForm();
        closeModal();
      } catch (e: any) {
        const message = e?.data?.message ?? 'Error scheduling one-time payment';
        toast.error(message);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!isModalOpen) {
      formik.resetForm();
      setShowCardNumber(false);
      setShowCvc(false);
      setShowAccountNumber(false);
      setPaymentType('card');
    }
  }, [isModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    formik.resetForm({ values: formik.values });
  }, [paymentType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePresetChange = useCallback(
    (preset: 'pastDue' | 'total' | 'custom') => {
      setAmountPreset(preset);
      if (preset === 'pastDue' && amountPastDue) {
        formik.setFieldValue('amount', parseFloat(amountPastDue).toFixed(2));
      } else if (preset === 'total' && defaultAmount) {
        formik.setFieldValue('amount', parseFloat(defaultAmount).toFixed(2));
      } else {
        formik.setFieldValue('amount', '');
      }
      formik.setFieldTouched('amount', false);
    },
    [amountPastDue, defaultAmount, formik],
  );

  const handleDigitsOnly = useCallback(
    (field: string, value: string, maxLength: number, nextFieldId?: string, advanceAt?: number) => {
      const digits = value.replace(/\D/g, '').slice(0, maxLength);
      formik.setFieldValue(field, digits);
      const threshold = advanceAt ?? maxLength;
      if (nextFieldId && digits.length >= threshold) {
        focusField(nextFieldId);
      }
    },
    [formik],
  );

  const hasPendingPayment =
    !isCheckingPending &&
    financingData?.hasScheduledOneTimePayment &&
    (financingData.pendingPayments?.length ?? 0) > 0;

  const pendingPayment = hasPendingPayment ? financingData!.pendingPayments![0] : null;

  return (
    <Modal ariaLabelledBy="oneTimePaymentModal" isOpen={isModalOpen} onDismiss={closeModal} maxWidth="640px">
      <Box
        id="oneTimePaymentModal"
        padding="lg"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        borderColor="separator"
        borderWidth="0 0 xs 0"
        style={{ flexShrink: 0 }}
      >
        <Box>
          <Box as="h4" fontSize={{ base: 'md', tablet: 'lg' }}>
            Make One-Time Payment
          </Box>
        </Box>
        <button
          aria-label="close"
          type="button"
          onClick={closeModal}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: 'var(--size-spacing-xs)',
            color: 'var(--color-text-body-primary)',
            lineHeight: 'var(--size-line-height-base)',
            borderRadius: 'var(--size-border-radius-sm)',
          }}
        >
          <Icon name="remove-light" />
        </button>
      </Box>
      {hasPendingPayment && pendingPayment ? (
        <>
          <Modal.Body background="grey-50">
            <Box direction="column" childGap="md">
              <Box
                background="warning-100"
                borderColor="warning-300"
                borderWidth="xs"
                radius="md"
                padding="md"
                fontWeight="bold"
                color="warning-700"
              >
                A payment is already scheduled
              </Box>
              <Box
                background="white"
                borderColor="separator"
                borderWidth="xs"
                radius="md"
                padding="md"
                direction="column"
                childGap="sm"
                fontSize="sm"
              >
                <Box direction="row" justifyContent="space-between">
                  <Box color="grey-500">Amount</Box>
                  <Box fontWeight="bold">${pendingPayment.amount.toFixed(2)}</Box>
                </Box>
                <Box direction="row" justifyContent="space-between">
                  <Box color="grey-500">Scheduled</Box>
                  <Box>{DateTime.fromISO(pendingPayment.paymentDate).toLocaleString(DateTime.DATE_MED)}</Box>
                </Box>
                <Box direction="row" justifyContent="space-between">
                  <Box color="grey-500">Type</Box>
                  <Box>{pendingPayment.paymentTypeDescription}</Box>
                </Box>
                <Box direction="row" justifyContent="space-between">
                  <Box color="grey-500">Entered on</Box>
                  <Box>{DateTime.fromISO(pendingPayment.entryDate).toLocaleString(DateTime.DATE_MED)}</Box>
                </Box>
              </Box>
              <Box fontSize="sm" color="grey-600">
                Cancel this payment before scheduling a new one.
              </Box>
            </Box>
          </Modal.Body>
          <Modal.Footer>
            <Button as="button" onClick={closeModal} variant="secondary" tone="neutral" size="md">
              Close
            </Button>
            <Button variant="primary" tone="neutral" size="md" type="button" onClick={handleOpenCancelForPending}>
              Cancel Payment
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <form onSubmit={formik.handleSubmit} style={{ display: 'contents' }}>
          <Modal.Body background="grey-50" style={{ fontSize: '14px' }}>
            <Box direction="column" childGap="sm">
              {isCheckingPending && (
                <Box padding="md" background="grey-100" radius="md" fontSize="sm" color="grey-600">
                  Checking for existing scheduled payments...
                </Box>
              )}

              <Box
                background="white"
                borderColor="separator"
                borderWidth="xs"
                radius="md"
                padding="sm"
                direction="row"
                childGap="xs"
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    background: paymentType === 'card' ? 'var(--color-brand-primary-50)' : 'transparent',
                    cursor: 'pointer',
                    flex: 1,
                  }}
                  onClick={() => setPaymentType('card')}
                >
                  <input
                    type="radio"
                    name="paymentType"
                    checked={paymentType === 'card'}
                    onChange={() => setPaymentType('card')}
                    style={{
                      accentColor: 'var(--color-brand-primary-500)',
                      width: '16px',
                      height: '16px',
                      flexShrink: 0,
                      cursor: 'pointer',
                    }}
                  />
                  <Box fontSize="sm" fontWeight={paymentType === 'card' ? 'medium' : 'regular'}>
                    Credit Card
                  </Box>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    background: paymentType === 'ach' ? 'var(--color-brand-primary-50)' : 'transparent',
                    cursor: 'pointer',
                    flex: 1,
                  }}
                  onClick={() => setPaymentType('ach')}
                >
                  <input
                    type="radio"
                    name="paymentType"
                    checked={paymentType === 'ach'}
                    onChange={() => setPaymentType('ach')}
                    style={{
                      accentColor: 'var(--color-brand-primary-500)',
                      width: '16px',
                      height: '16px',
                      flexShrink: 0,
                      cursor: 'pointer',
                    }}
                  />
                  <Box fontSize="sm" fontWeight={paymentType === 'ach' ? 'medium' : 'regular'}>
                    Bank Account (ACH)
                  </Box>
                </div>
              </Box>

              {paymentType === 'card' && (
                <Box direction="column" childGap="sm">
                  <TextInput
                    label="Card Holder Name"
                    name="cardHolderName"
                    id="cardHolderName"
                    size="sm"
                    autoFocus
                    value={formik.values.cardHolderName}
                    onChange={(e) => formik.setFieldValue('cardHolderName', e.target.value)}
                    onBlur={() => formik.setFieldTouched('cardHolderName')}
                    error={formik.touched.cardHolderName && (formik.errors.cardHolderName as string)}
                  />

                  <TextInput
                    label="Card Number"
                    name="number"
                    id="number"
                    size="sm"
                    type={showCardNumber ? 'text' : formik.values.number ? 'password' : 'text'}
                    value={formik.values.number}
                    onChange={(e) => handleDigitsOnly('number', e.target.value, 19)}
                    onFocus={() => setShowCardNumber(true)}
                    onBlur={() => {
                      setShowCardNumber(false);
                      formik.setFieldTouched('number');
                    }}
                    suffix={
                      <Icon
                        size="sm"
                        name={showCardNumber ? 'eye' : 'eye-slash'}
                        onClick={() => setShowCardNumber((prev) => !prev)}
                        style={{ cursor: 'pointer' }}
                      />
                    }
                    error={formik.touched.number && (formik.errors.number as string)}
                  />

                  <Box direction={{ base: 'column', tablet: 'row' }} childGap="md">
                    <SelectInput
                      label="Exp. Month"
                      name="expirationMonth"
                      id="expirationMonth"
                      size="sm"
                      value={MONTH_OPTIONS.find((o) => o.value === formik.values.expirationMonth) ?? null}
                      options={MONTH_OPTIONS}
                      onChange={(e) => {
                        formik.setFieldValue('expirationMonth', e.target.value.value);
                        focusField('expirationYear');
                      }}
                      onBlur={() => formik.setFieldTouched('expirationMonth')}
                      error={formik.touched.expirationMonth && (formik.errors.expirationMonth as string)}
                      menuPortalTarget={document.body}
                    />
                    <SelectInput
                      label="Exp. Year"
                      name="expirationYear"
                      id="expirationYear"
                      size="sm"
                      value={YEAR_OPTIONS.find((o) => o.value === formik.values.expirationYear) ?? null}
                      options={YEAR_OPTIONS}
                      onChange={(e) => {
                        formik.setFieldValue('expirationYear', e.target.value.value);
                        focusField('cvc');
                      }}
                      onBlur={() => formik.setFieldTouched('expirationYear')}
                      error={formik.touched.expirationYear && (formik.errors.expirationYear as string)}
                      menuPortalTarget={document.body}
                    />
                  </Box>

                  <Box direction={{ base: 'column', tablet: 'row' }} childGap="md">
                    <TextInput
                      label="Billing Zip Code"
                      name="zipCode"
                      id="zipCode"
                      size="sm"
                      value={formik.values.zipCode}
                      onChange={(e) => handleDigitsOnly('zipCode', e.target.value, 5, 'cvc')}
                      onBlur={() => formik.setFieldTouched('zipCode')}
                      error={formik.touched.zipCode && (formik.errors.zipCode as string)}
                    />

                    <TextInput
                      label="CVC"
                      name="cvc"
                      id="cvc"
                      size="sm"
                      type={showCvc ? 'text' : formik.values.cvc ? 'password' : 'text'}
                      value={formik.values.cvc}
                      onChange={(e) => handleDigitsOnly('cvc', e.target.value, 4, 'amount', 3)}
                      onFocus={() => setShowCvc(true)}
                      onBlur={() => {
                        setShowCvc(false);
                        formik.setFieldTouched('cvc');
                      }}
                      suffix={
                        <Icon
                          size="sm"
                          name={showCvc ? 'eye' : 'eye-slash'}
                          onClick={() => setShowCvc((prev) => !prev)}
                          style={{ cursor: 'pointer' }}
                        />
                      }
                      error={formik.touched.cvc && (formik.errors.cvc as string)}
                    />
                  </Box>
                </Box>
              )}

              {paymentType === 'ach' && (
                <Box direction="column" childGap="sm">
                  <TextInput
                    label="Account Holder Name"
                    name="accountHolderName"
                    id="accountHolderName"
                    size="sm"
                    autoFocus
                    value={formik.values.accountHolderName}
                    onChange={(e) => formik.setFieldValue('accountHolderName', e.target.value)}
                    onBlur={() => formik.setFieldTouched('accountHolderName')}
                    error={formik.touched.accountHolderName && (formik.errors.accountHolderName as string)}
                  />

                  <TextInput
                    label="Routing Number"
                    name="routingNumber"
                    id="routingNumber"
                    size="sm"
                    maxLength={9}
                    value={formik.values.routingNumber}
                    onChange={(e) => handleDigitsOnly('routingNumber', e.target.value, 9)}
                    onBlur={() => formik.setFieldTouched('routingNumber')}
                    error={formik.touched.routingNumber && (formik.errors.routingNumber as string)}
                  />

                  <TextInput
                    label="Account Number"
                    name="accountNumber"
                    id="accountNumber"
                    size="sm"
                    type={showAccountNumber ? 'text' : formik.values.accountNumber ? 'password' : 'text'}
                    maxLength={17}
                    value={formik.values.accountNumber}
                    onChange={(e) => handleDigitsOnly('accountNumber', e.target.value, 17)}
                    onFocus={() => setShowAccountNumber(true)}
                    onBlur={() => {
                      setShowAccountNumber(false);
                      formik.setFieldTouched('accountNumber');
                    }}
                    suffix={
                      <Icon
                        size="sm"
                        name={showAccountNumber ? 'eye' : 'eye-slash'}
                        onClick={() => setShowAccountNumber((prev) => !prev)}
                        style={{ cursor: 'pointer' }}
                      />
                    }
                    error={formik.touched.accountNumber && (formik.errors.accountNumber as string)}
                  />

                  <TextInput
                    label="Confirm Account Number"
                    name="confirmAccountNumber"
                    id="confirmAccountNumber"
                    size="sm"
                    maxLength={17}
                    value={formik.values.confirmAccountNumber}
                    onChange={(e) => handleDigitsOnly('confirmAccountNumber', e.target.value, 17)}
                    onBlur={() => formik.setFieldTouched('confirmAccountNumber')}
                    error={formik.touched.confirmAccountNumber && (formik.errors.confirmAccountNumber as string)}
                  />

                  <SelectInput
                    label="Account Type"
                    name="bankAccountType"
                    id="bankAccountType"
                    size="sm"
                    value={BANK_ACCOUNT_TYPE_OPTIONS.find((o) => o.value === formik.values.bankAccountType) ?? null}
                    options={BANK_ACCOUNT_TYPE_OPTIONS}
                    onChange={(e) => formik.setFieldValue('bankAccountType', e.target.value.value)}
                    onBlur={() => formik.setFieldTouched('bankAccountType')}
                    error={formik.touched.bankAccountType && (formik.errors.bankAccountType as string)}
                    menuPortalTarget={document.body}
                  />
                </Box>
              )}

              <DateInput
                dateFormat="MM/dd/yyyy"
                suffix={<Icon name="calendar" />}
                popoverProps={{ placement: 'bottom-end', hasArrow: false }}
                textInputProps={{
                  id: 'paymentDate',
                  name: 'paymentDate',
                  label: 'Payment Date',
                  size: 'sm',
                  onBlur: () => formik.setFieldTouched('paymentDate'),
                  error: formik.touched.paymentDate && (formik.errors.paymentDate as string),
                }}
                datePickerProps={{
                  selected: formik.values.paymentDate ?? undefined,
                  onChange: (date: Date | [Date, Date] | null) =>
                    formik.setFieldValue('paymentDate', Array.isArray(date) ? date[0] : date),
                  minDate: startOfToday(),
                  shouldCloseOnSelect: true,
                }}
              />

              {defaultAmount || amountPastDue ? (
                <Box direction="column" childGap="sm">
                  <Box fontSize="sm" fontWeight="medium" color="grey-700">
                    Amount
                  </Box>
                  <Box direction="column" childGap="xs">
                    {amountPastDue && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 12px',
                          borderRadius: '6px',
                          background: amountPreset === 'pastDue' ? 'var(--color-brand-primary-50)' : 'transparent',
                        }}
                      >
                        <input
                          type="radio"
                          name="amountPreset"
                          checked={amountPreset === 'pastDue'}
                          onChange={() => handlePresetChange('pastDue')}
                          style={{
                            accentColor: 'var(--color-brand-primary-500)',
                            width: '16px',
                            height: '16px',
                            flexShrink: 0,
                            cursor: 'pointer',
                          }}
                        />
                        <Box direction="row" childGap="xs" alignItems="center">
                          <Box fontSize="sm" color="grey-600">
                            Past due balance:
                          </Box>
                          <Box fontSize="sm" fontWeight="bold" color="grey-800">
                            ${parseFloat(amountPastDue).toFixed(2)}
                          </Box>
                        </Box>
                      </div>
                    )}
                    {defaultAmount && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 12px',
                          borderRadius: '6px',
                          background: amountPreset === 'total' ? 'var(--color-brand-primary-50)' : 'transparent',
                        }}
                      >
                        <input
                          type="radio"
                          name="amountPreset"
                          checked={amountPreset === 'total'}
                          onChange={() => handlePresetChange('total')}
                          style={{
                            accentColor: 'var(--color-brand-primary-500)',
                            width: '16px',
                            height: '16px',
                            flexShrink: 0,
                            cursor: 'pointer',
                          }}
                        />
                        <Box direction="row" childGap="xs" alignItems="center">
                          <Box fontSize="sm" color="grey-600">
                            Total balance:
                          </Box>
                          <Box fontSize="sm" fontWeight="bold" color="grey-800">
                            ${parseFloat(defaultAmount).toFixed(2)}
                          </Box>
                        </Box>
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        background: amountPreset === 'custom' ? 'var(--color-brand-primary-50)' : 'transparent',
                      }}
                    >
                      <input
                        type="radio"
                        name="amountPreset"
                        checked={amountPreset === 'custom'}
                        onChange={() => handlePresetChange('custom')}
                        style={{
                          accentColor: 'var(--color-brand-primary-500)',
                          width: '16px',
                          height: '16px',
                          flexShrink: 0,
                          cursor: 'pointer',
                        }}
                      />
                      {amountPreset === 'custom' ? (
                        <Box flex="auto">
                          <TextInput
                            label="Custom Amount"
                            name="amount"
                            id="amount"
                            size="sm"
                            inputMode="decimal"
                            value={formik.values.amount}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/[^0-9.]/g, '');
                              const parts = raw.split('.');
                              const sanitized =
                                parts.length > 2
                                  ? `${parts[0]}.${parts.slice(1).join('')}`
                                  : parts[1] !== undefined
                                    ? `${parts[0]}.${parts[1].slice(0, 2)}`
                                    : raw;
                              formik.setFieldValue('amount', sanitized);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched('amount');
                              const num = parseFloat(formik.values.amount);
                              if (!isNaN(num) && num > 0) {
                                formik.setFieldValue('amount', num.toFixed(2));
                              }
                            }}
                            error={formik.touched.amount && (formik.errors.amount as string)}
                          />
                        </Box>
                      ) : (
                        <Box fontSize="sm" color="grey-600">
                          Custom amount
                        </Box>
                      )}
                    </div>
                  </Box>
                  {amountPreset !== 'custom' && formik.submitCount > 0 && formik.errors.amount && (
                    <Box fontSize="sm" color="danger-500">
                      {formik.errors.amount as string}
                    </Box>
                  )}
                </Box>
              ) : (
                <TextInput
                  label="Amount"
                  name="amount"
                  id="amount"
                  size="sm"
                  inputMode="decimal"
                  value={formik.values.amount}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9.]/g, '');
                    const parts = raw.split('.');
                    const sanitized =
                      parts.length > 2
                        ? `${parts[0]}.${parts.slice(1).join('')}`
                        : parts[1] !== undefined
                          ? `${parts[0]}.${parts[1].slice(0, 2)}`
                          : raw;
                    formik.setFieldValue('amount', sanitized);
                  }}
                  onBlur={() => {
                    formik.setFieldTouched('amount');
                    const num = parseFloat(formik.values.amount);
                    if (!isNaN(num) && num > 0) {
                      formik.setFieldValue('amount', num.toFixed(2));
                    }
                  }}
                  error={formik.touched.amount && (formik.errors.amount as string)}
                />
              )}
            </Box>
          </Modal.Body>
          <Modal.Footer>
            <Button
              as="button"
              onClick={() => {
                formik.resetForm();
                setShowCardNumber(false);
                setShowCvc(false);
                setShowAccountNumber(false);
                setAmountPreset(getDefaultPreset());
                setPaymentType('card');
                closeModal();
              }}
              variant="secondary"
              tone="neutral"
              size="md"
              isDisabled={formik.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              type="submit"
              isLoading={formik.isSubmitting}
              isDisabled={formik.isSubmitting || isCheckingPending || !!financingData?.hasScheduledOneTimePayment}
            >
              Submit Payment
            </Button>
          </Modal.Footer>
        </form>
      )}
      {cancelTarget && (
        <CancelPendingPaymentModal
          isOpen
          onClose={closeCancelModal}
          onCancelled={handleCancelled}
          payment={cancelTarget}
        />
      )}
    </Modal>
  );
};
